import type { Locale } from "@/types";
import { isVideoAnalysisEnabled, isVideoSearchEnabled } from "@/config/features";

export type ToolId =
  | "image-generator"
  | "video-search"
  | "video-analysis"
  | "creative-signal-scanner"
  | "video-breakdown"
  | "script-brief-builder"
  | "creator-fit-checklist";

export type ToolStatus = "early-access" | "in-development" | "coming-soon";

export interface ToolDefinition {
  id: ToolId;
  label: string;
  shortDescription: string;
  description: string;
  status: ToolStatus;
  inputs: readonly string[];
  process: readonly string[];
  outputs: readonly string[];
  handoff: string;
}

export const toolsByLocale: Record<Locale, readonly ToolDefinition[]> = {
  en: [
    {
      id: "image-generator",
      label: "Image Generator",
      shortDescription: "Turn a creative direction into production-ready image options.",
      description: "Build product stills, campaign concepts, and editorial imagery from a prompt, optional reference, model, format, and output settings.",
      status: "early-access",
      inputs: ["Creative direction", "Optional reference image", "Format and model"],
      process: ["Choose a starting direction", "Refine the prompt and format", "Generate reviewable variations"],
      outputs: ["Image variations", "Reusable prompt", "Generation settings"],
      handoff: "Selected images move into the shared asset library with their prompt and generation context attached.",
    },
    {
      id: "video-search",
      label: "Video Search",
      shortDescription: "Find videos worth studying across the platforms your audience uses.",
      description: "Search public video references by creative intent and platform, then compare useful examples in one focused workspace.",
      status: "early-access",
      inputs: ["Creative search intent", "One platform", "Up to 5 references"],
      process: ["Describe the research question", "Choose one platform", "Review relevant videos"],
      outputs: ["Source links", "Platform context", "Saved references"],
      handoff: "Selected references can continue into video breakdown and script brief workflows.",
    },
    {
      id: "video-analysis",
      label: "Video Analysis",
      shortDescription: "Turn one reference video into structured creative insight.",
      description: "Analyze a public TikTok, Instagram or YouTube video link to understand its hook, structure, pacing and reusable creative mechanics.",
      status: "early-access",
      inputs: ["Public video link", "TikTok, Instagram or YouTube", "One reference at a time"],
      process: ["Validate the source", "Observe the creative structure", "Organize the evidence"],
      outputs: ["Creative summary", "Hook and scene structure", "Reusable mechanics"],
      handoff: "The analysis can continue into creative direction and script development in the workspace.",
    },
    {
      id: "creative-signal-scanner",
      label: "Creative Signal Scanner",
      shortDescription: "Rank market patterns worth investigating.",
      description: "Turn a category, market, or competitor question into a ranked set of creative patterns, with the source and selection reason kept attached.",
      status: "coming-soon",
      inputs: ["Category or competitor", "Target market", "Research question"],
      process: ["Scan sample evidence", "Group repeated mechanics", "Rank by brand relevance"],
      outputs: ["Ranked signals", "Source references", "Selection rationale"],
      handoff: "The strongest signals move into video breakdown and creative direction.",
    },
    {
      id: "video-breakdown",
      label: "Video Breakdown",
      shortDescription: "Map why a reference video works, scene by scene.",
      description: "Break a reference video into its hook, pacing, proof, narrative turn, visual devices, offer, and audience response.",
      status: "coming-soon",
      inputs: ["Reference video", "Brand question", "Audience context"],
      process: ["Mark scene changes", "Label creative mechanics", "Separate evidence from execution"],
      outputs: ["Scene timeline", "Mechanic analysis", "Reusable direction"],
      handoff: "Approved mechanics move to the Script Brief Builder without copying the source execution.",
    },
    {
      id: "script-brief-builder",
      label: "Script Brief Builder",
      shortDescription: "Turn evidence into a brief a creator can shoot.",
      description: "Combine a product fact, audience tension, creative mechanic, and production constraint into a structured, brand-ready brief.",
      status: "coming-soon",
      inputs: ["Product fact", "Audience tension", "Creative mechanic", "Production constraint"],
      process: ["Shape the hook", "Sequence proof and payoff", "Check brand constraints"],
      outputs: ["Hook", "Scene plan", "Proof points", "Call to action"],
      handoff: "The approved brief moves to creator matching and production review.",
    },
    {
      id: "creator-fit-checklist",
      label: "Creator Fit Checklist",
      shortDescription: "Match the creative to the right delivery style.",
      description: "Evaluate creator candidates against the concept, delivery style, audience, market, and operational requirements instead of follower count alone.",
      status: "coming-soon",
      inputs: ["Approved concept", "Audience and market", "Creator candidates", "Production needs"],
      process: ["Compare audience fit", "Review delivery style", "Check practical constraints"],
      outputs: ["Fit profile", "Match rationale", "Review flags", "Creator brief"],
      handoff: "A reviewable shortlist returns to the Project Manager with the reasoning intact.",
    },
  ],
  zh: [
    {
      id: "image-generator",
      label: "生图工具",
      shortDescription: "把创意方向转化为可评审、可交付的图片方案。",
      description: "通过提示词、参考图、模型、画幅和输出设置，生成产品图、Campaign 概念图与编辑视觉。",
      status: "early-access",
      inputs: ["创意方向", "可选参考图", "画幅与模型"],
      process: ["选择起始方向", "完善提示词与规格", "生成可评审方案"],
      outputs: ["图片方案", "可复用提示词", "生成参数"],
      handoff: "选中的图片会进入共享资产库，并保留提示词和生成参数。",
    },
    {
      id: "video-search",
      label: "视频搜索",
      shortDescription: "在受众使用的平台中找到值得研究的视频。",
      description: "按创意查询意图和平台搜索公开视频，在一个聚焦的结果视图中比较有价值的参考内容。",
      status: "early-access",
      inputs: ["创意查询意图", "单个平台", "最多 5 个参考"],
      process: ["描述研究问题", "选择平台", "查看相关视频"],
      outputs: ["原始链接", "平台背景", "收藏参考"],
      handoff: "选中的视频参考可以进入视频拆解和脚本 Brief 工作流。",
    },
    {
      id: "video-analysis",
      label: "视频解析",
      shortDescription: "把一条参考视频转化为结构化创意洞察。",
      description: "解析 TikTok、Instagram 或 YouTube 的公开视频链接，理解 Hook、内容结构、节奏与可复用的创意机制。",
      status: "early-access",
      inputs: ["公开视频链接", "TikTok、Instagram 或 YouTube", "每次一条参考"],
      process: ["校验视频来源", "观察创意结构", "整理视频证据"],
      outputs: ["创意摘要", "Hook 与场景结构", "可复用机制"],
      handoff: "解析结果可以继续进入工作台的创意方向与脚本创作。",
    },
    {
      id: "creative-signal-scanner",
      label: "创意信号扫描器",
      shortDescription: "筛选并排序值得深入研究的市场模式。",
      description: "把品类、市场或竞品问题转化为创意模式清单，并保留来源与入选理由。",
      status: "coming-soon",
      inputs: ["品类或竞品", "目标市场", "研究问题"],
      process: ["扫描示例证据", "归纳重复机制", "按品牌相关性排序"],
      outputs: ["信号排名", "来源参考", "入选理由"],
      handoff: "最强信号会进入视频拆解与创意方向。",
    },
    {
      id: "video-breakdown",
      label: "视频拆解",
      shortDescription: "逐场景解释参考视频为什么有效。",
      description: "拆解参考视频的 Hook、节奏、证明、叙事转折、视觉机制、Offer 与受众反馈。",
      status: "coming-soon",
      inputs: ["参考视频", "品牌问题", "受众背景"],
      process: ["标记场景变化", "识别创意机制", "区分证据与具体执行"],
      outputs: ["场景时间线", "机制分析", "可复用方向"],
      handoff: "通过审核的机制进入脚本 Brief，避免照搬原视频。",
    },
    {
      id: "script-brief-builder",
      label: "脚本 Brief 生成器",
      shortDescription: "把证据变成创作者可以拍摄的 Brief。",
      description: "组合产品事实、受众张力、创意机制与制作限制，形成结构化品牌 Brief。",
      status: "coming-soon",
      inputs: ["产品事实", "受众张力", "创意机制", "制作限制"],
      process: ["设计 Hook", "组织证明与结果", "检查品牌限制"],
      outputs: ["Hook", "场景计划", "证明点", "CTA"],
      handoff: "通过审核的 Brief 会进入达人匹配与制作审核。",
    },
    {
      id: "creator-fit-checklist",
      label: "达人适配清单",
      shortDescription: "让创意与合适的表达方式匹配。",
      description: "按照创意、表达风格、受众、市场与执行条件评估达人，而不是只看粉丝量。",
      status: "coming-soon",
      inputs: ["已批准创意", "受众与市场", "达人候选", "制作需求"],
      process: ["比较受众适配", "审核表达风格", "检查执行条件"],
      outputs: ["适配概况", "匹配理由", "审核提示", "Creator Brief"],
      handoff: "保留完整依据的候选名单会回到项目经理审核。",
    },
  ],
};

export const toolPages = toolsByLocale.en;

export function getVisibleTools(locale: Locale, mode = import.meta.env.MODE) {
  return toolsByLocale[locale].filter((tool) => {
    if (tool.id === "video-search") return isVideoSearchEnabled(mode);
    if (tool.id === "video-analysis") return isVideoAnalysisEnabled(mode);
    return true;
  });
}

export function getToolStatusLabel(status: ToolStatus, locale: Locale = "en") {
  if (locale === "zh") {
    if (status === "coming-soon") return "敬请期待";
    return status === "early-access" ? "早期体验" : "开发中";
  }
  if (status === "coming-soon") return "Coming soon";
  return status === "early-access" ? "Early access" : "In development";
}
